import "./styles/app.css";
import "./styles/board.css";
import { getSupabaseClient } from "./lib/supabase.js";
import { getSession, onAuthStateChange, signInWithGoogle, signOut } from "./auth/session.js";
import { isEmailAllowlisted } from "./auth/allowlist.js";
import { upsertProfile } from "./auth/profile.js";
import { renderPairHub } from "./ui/pairHub.js";
import { mountGameRoom } from "./ui/gameRoom.js";

const app = document.getElementById("app");
if (!app) {
  throw new Error("#app missing");
}

function render(html) {
  app.innerHTML = html;
}

function renderSignIn(onClick) {
  render(`
    <div class="auth-card">
      <h1>Backgammon</h1>
      <p>Sign in with Google (allowlisted accounts only).</p>
      <button type="button" id="sign-in-google">Sign in with Google</button>
    </div>
  `);
  document.getElementById("sign-in-google")?.addEventListener("click", onClick);
}

function renderUnauthorized(email) {
  render(`
    <div class="unauthorized">
      <h1>Not authorized</h1>
      <p>The account <strong>${email ?? "unknown"}</strong> is not on the allowlist.</p>
      <p>Contact the administrator to add your email to <code>allowed_users</code>.</p>
    </div>
  `);
}

function renderSignedInHeader(email, onSignOut) {
  const wrap = document.createElement("div");
  wrap.className = "auth-card";
  wrap.innerHTML = `<p>Signed in as <strong>${email}</strong></p>`;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Sign out";
  btn.addEventListener("click", onSignOut);
  wrap.appendChild(btn);
  return wrap;
}

async function boot() {
  let supabase;
  try {
    supabase = getSupabaseClient();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    render(`<div class="auth-card"><h1>Configuration</h1><p>${msg}</p></div>`);
    return;
  }

  /** @type {{ destroy?: () => void } | null} */
  let room = null;

  async function showPairHub(userId, email) {
    if (room) {
      room.destroy?.();
      room = null;
    }
    app.replaceChildren();
    app.appendChild(renderSignedInHeader(email, () => void signOut(supabase).then(() => {})));
    const hub = await renderPairHub({
      supabase,
      userId,
      onEnterGame: async (detail) => {
        app.replaceChildren();
        app.appendChild(renderSignedInHeader(email, () => void signOut(supabase).then(() => {})));
        const roomRoot = document.createElement("div");
        roomRoot.className = "game-room";
        app.appendChild(roomRoot);
        room = await mountGameRoom(roomRoot, {
          supabase,
          userId,
          pairingId: detail.pairingId,
          gameId: detail.gameId,
          mode: detail.mode === "resume" ? "resume" : "new",
          onLeave: () => {
            void showPairHub(userId, email);
          },
        });
      },
    });
    app.appendChild(hub);
  }

  async function route(session) {
    if (!session?.user) {
      renderSignIn(() => void signInWithGoogle(supabase));
      return;
    }
    const email = session.user.email ?? undefined;
    const allowed = await isEmailAllowlisted(supabase, email);
    if (!allowed) {
      renderUnauthorized(email);
      return;
    }
    await upsertProfile(supabase, session.user);
    await showPairHub(session.user.id, email ?? "");
  }

  await route(await getSession(supabase));
  onAuthStateChange(supabase, (_event, session) => {
    void route(session);
  });
}

void boot();
